import { Button, Flex, Text, VStack } from "@chakra-ui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ToggleButton } from "src/components/Buttons/ToggleButton";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { ResizableDrawer } from "src/components/Layout/ResizableDrawer";
import { SearchAndFilterTypes } from "src/constants";
import { ITEM_STATUS_OPTIONS } from "./labels";

interface DetailFiltersProps {
  isOpen: boolean;
  schools: { id: string; name: string }[];
  messages: string[];
  onToggleFilter: () => void;
  onClearFilters: () => void;
}

const MESSAGE_SEPARATOR = "|";
const FILTER_PARAMS = ["schoolId", "status", "message"];

export const DetailFilters: React.FC<DetailFiltersProps> = ({
  isOpen,
  schools,
  messages,
  onToggleFilter,
  onClearFilters,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDirty, setIsDirty] = useState(false);
  const [localSearchParams, setLocalSearchParams] = useState<URLSearchParams>(
    new URLSearchParams(searchParams)
  );

  useEffect(() => {
    setLocalSearchParams(new URLSearchParams(searchParams));
    setIsDirty(false);
  }, [searchParams]);

  const selectedSchoolIds = useMemo(
    () => localSearchParams.get("schoolId")?.split(",") ?? [],
    [localSearchParams]
  );
  const selectedStatuses = useMemo(
    () => localSearchParams.get("status")?.split(",") ?? [],
    [localSearchParams]
  );
  const selectedMessages = useMemo(
    () => localSearchParams.get("message")?.split(MESSAGE_SEPARATOR) ?? [],
    [localSearchParams]
  );
  const schoolOptions = useMemo(
    () => schools.map((school) => ({ id: school.id, label: school.name })),
    [schools]
  );
  const selectedSchools = useMemo(
    () =>
      schoolOptions.filter((school) => selectedSchoolIds.includes(school.id)),
    [schoolOptions, selectedSchoolIds]
  );

  const handleToggle = useCallback(
    (
      param: string,
      selected: string[],
      value: string,
      separator: string = ","
    ) => {
      const newValues = selected.includes(value)
        ? selected.filter((entry) => entry !== value)
        : [...selected, value];

      const newParams = new URLSearchParams(localSearchParams);
      if (newValues.length === 0) {
        newParams.delete(param);
      } else {
        newParams.set(param, newValues.join(separator));
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams]
  );

  const handleSchoolsChange = useCallback(
    (selected: { id: string; label: string }[]) => {
      const newParams = new URLSearchParams(localSearchParams);
      if (selected.length === 0) {
        newParams.delete("schoolId");
      } else {
        newParams.set(
          "schoolId",
          selected.map((school) => school.id).join(",")
        );
      }
      newParams.delete(SearchAndFilterTypes.Offset);
      setLocalSearchParams(newParams);
      setIsDirty(true);
    },
    [localSearchParams]
  );

  const handleApplyFilters = useCallback(() => {
    setSearchParams(localSearchParams);
    setIsDirty(false);
    onToggleFilter();
  }, [localSearchParams, setSearchParams, onToggleFilter]);

  const handleClearAll = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    FILTER_PARAMS.forEach((param) => newParams.delete(param));
    newParams.delete(SearchAndFilterTypes.Offset);
    setSearchParams(newParams);
    setLocalSearchParams(newParams);
    setIsDirty(false);
    onClearFilters();
    onToggleFilter();
  }, [searchParams, setSearchParams, onClearFilters, onToggleFilter]);

  const hasActiveFilters = FILTER_PARAMS.some((param) =>
    searchParams.get(param)
  );

  return (
    <ResizableDrawer
      autosaveId="formActivityDetailFilterDrawerWidth"
      isOpen={isOpen}
      onClose={onToggleFilter}
      header="Filter"
      footer={
        <>
          <Button
            onClick={handleClearAll}
            variant="outline"
            colorScheme="gray"
            flex="1"
            isDisabled={!hasActiveFilters && !isDirty}
          >
            Clear all
          </Button>
          <Button
            onClick={handleApplyFilters}
            variant="solid"
            colorScheme="primary"
            flex="1"
            isDisabled={!isDirty}
          >
            Apply
          </Button>
        </>
      }
    >
      <VStack align="stretch" spacing={6}>
        {schools.length > 0 && (
          <VStack align="stretch" spacing={2}>
            <Text variant="filterLabel">School</Text>
            <MultiSelectInput
              size="md"
              options={schoolOptions}
              initialValues={selectedSchools}
              onChange={handleSchoolsChange}
              placeholder="Type a school"
            />
          </VStack>
        )}

        <VStack align="stretch" spacing={2}>
          <Text variant="filterLabel">Status</Text>
          <Flex flexWrap="wrap" gap={2}>
            {ITEM_STATUS_OPTIONS.map((option) => (
              <ToggleButton
                key={option.id}
                active={selectedStatuses.includes(option.id)}
                onClick={() =>
                  handleToggle("status", selectedStatuses, option.id)
                }
              >
                {option.label}
              </ToggleButton>
            ))}
          </Flex>
        </VStack>

        {messages.length > 0 && (
          <VStack align="stretch" spacing={2}>
            <Text variant="filterLabel">Error</Text>
            <Flex flexWrap="wrap" gap={2}>
              {messages.map((message) => (
                <ToggleButton
                  key={message}
                  active={selectedMessages.includes(message)}
                  onClick={() =>
                    handleToggle(
                      "message",
                      selectedMessages,
                      message,
                      MESSAGE_SEPARATOR
                    )
                  }
                >
                  {message}
                </ToggleButton>
              ))}
            </Flex>
          </VStack>
        )}
      </VStack>
    </ResizableDrawer>
  );
};
